import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { MatOption, MatSelect, MatSelectTrigger } from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { finalize, forkJoin, Observable } from 'rxjs';
import { NumbersOnlyDirective } from '../../directives/numbers-only.directive';
import { Entity, EntityKey } from '../../entity/entity';
import { Competitor } from '../../model/competitor';
import { ExternalSpecial } from '../../model/external-special';
import { ExternalSpecialLineAssignment } from '../../model/external-special-line-assignment';
import { ExternalSpecialRequestEntry } from '../../model/external-special-request-entry';
import { SpecialQuoteRequest } from '../../model/special-quote-request';
import { PersonLogin, RoleName } from '../../person/person-login';
import { DialogService } from '../../services/dialog.service';
import { EntityService } from '../../services/entity-service';
import { OrganizationService } from '../../services/organization-service';
import { PersonLoginService } from '../../services/person-login-service';
import { SpecialQuoteService } from '../../services/special-quote.service';
import { StringUtils } from '../../utils/string-utils';
import { FileUtils } from '../../utils/file-utils';

@Component({
  selector: 'app-special-quotes-dialog',
  templateUrl: './special-quotes-dialog.component.html',
  imports: [
    FormsModule,
    MatButton,
    NumbersOnlyDirective,
    MatIcon,
    MatTable,
    MatCell,
    MatHeaderCell,
    MatRow,
    MatHeaderRow,
    MatIconButton,
    MatColumnDef,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    MatSelect,
    MatOption,
    MatSelectTrigger,
    SlicePipe,
    MatTooltip,
    MatProgressSpinner,
    NgTemplateOutlet,
    MatTab,
    MatTabGroup,
    MatCheckbox,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './special-quotes-dialog.component.css',
})
export class SpecialQuotesDialogComponent {
  readonly dialogRef = inject<MatDialogRef<SpecialQuotesDialogComponent>>(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly entityService = inject(EntityService);
  private readonly organizationService = inject(OrganizationService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly specialQuoteService: SpecialQuoteService = inject(SpecialQuoteService);
  private readonly dialogService: DialogService = inject(DialogService);
  public entityKey: EntityKey;
  private personLogin: PersonLogin;
  protected readonly specialQuoteRequest = signal<SpecialQuoteRequest>(new SpecialQuoteRequest());
  protected readonly competitors = signal<Competitor[]>([]);
  protected readonly specialProducts = signal<string[]>([]);
  protected readonly errorMessage = signal<string>(undefined!);
  protected fileContent!: string;
  protected fileName!: string;
  @ViewChild('fileSelection') private fileSelection?: ElementRef<HTMLInputElement>;
  protected readonly entity = signal<Entity>(undefined!);
  protected readonly projectName = computed(() => this.entity()?.projectName);
  protected readonly numUnitsInProject = computed(() => this.getNumUnitsInProject(this.entity()));
  protected readonly projectValue = computed(() => this.entity()?.totalQuoteAmount);
  protected readonly isRepUser = signal<boolean>(false);
  protected readonly isUserCancelable = signal<boolean>(false);
  protected isITUser: boolean = false;
  protected isRSMUser: boolean = false;
  protected isAppEngUser: boolean = false;
  protected quoteNumber!: number;
  protected readonly isBusy = signal<boolean>(false);
  private readonly pendingLoads = signal<number>(0);
  protected readonly showSpinner = computed(() => this.isBusy() || this.pendingLoads() > 0);
  protected readonly externalSpecials = signal<ExternalSpecial[]>([]);
  protected readonly externalSpecialRequests = signal<ExternalSpecialRequestEntry[]>([]);
  protected displayedColumns: string[] = ['quoteNumber', 'description', 'pricePerUnit', 'setupFee', 'qty', 'status', 'actions'];
  protected readOnlyDisplayedColumns: string[] = ['quoteNumber', 'description', 'pricePerUnit', 'setupFee', 'qty', 'status'];
  /**
   * Raising and working special quote requests belongs to the reps and to application engineering, so
   * everyone else is kept out of the dialog rather than out of one section of it.
   */
  protected readonly hasSpecialQuoteAccess = computed(
    () => this.isRepUser() || this.isRSMUser || this.isITUser || this.isAppEngUser,
  );
  protected readonly showRequestTable = computed(() => this.externalSpecialRequests().length > 0);
  protected readonly showRequestForm = signal<boolean>(false);
  protected readonly isNewRequest = signal<boolean>(false);
  /**
   * The requests list is where the dialog opens for every entity, including orders and contracts, which
   * carry special quotes but no requests of their own. The form only takes over once a request is being
   * created or opened.
   */
  protected readonly showRequestList = computed(() => !this.showRequestForm());
  /** The workflow task state is only of use to the people who work the tasks. */
  protected readonly requestColumns = computed(() =>
    this.isITUser || this.isAppEngUser
      ? ['externalId', 'description', 'processStatus', 'taskOpen']
      : ['externalId', 'description', 'processStatus'],
  );
  /**
   * The special quotes the Manage Special Quotes section works on: the ones belonging to the request
   * currently open. Orders and older quotes carry specials without ever having had a request of their
   * own, so an entity with no requests falls back to all of them.
   */
  protected readonly selectedExternalSpecials = computed(() => {
    const externalSpecialRequestId = this.specialQuoteRequest().quoteNum;
    if (externalSpecialRequestId) {
      return this.externalSpecials().filter((item) => item.externalSpecialRequestId === String(externalSpecialRequestId));
    }
    return this.showRequestTable() ? [] : this.externalSpecials();
  });
  protected readonly showSpecialQuoteTable = computed(() => {
    if (!this.showRequestTable()) {
      return this.externalSpecials().length > 0;
    }
    const processingStatus = this.specialQuoteRequest().processingStatus;
    return !this.showRequestList() && (processingStatus === 'A' || processingStatus === 'E');
  });
  protected readonly entityLines = computed(() => this.entity()?.lines ?? []);
  protected readonly lineColumns: string[] = ['lineId', 'product', 'quantity'];
  protected readonly selectedLineId = signal<string | undefined>(undefined);
  /** Line id to the external ids assigned to it, as last loaded or saved. */
  private readonly savedLineAssignments = signal<Map<string, ReadonlySet<string>>>(new Map());
  /** Line id to the external ids assigned to it, including changes not yet saved. */
  protected readonly lineAssignments = signal<Map<string, ReadonlySet<string>>>(new Map());
  protected readonly selectedLineAssignments = computed(
    () => this.lineAssignments().get(this.selectedLineId() ?? '') ?? new Set<string>(),
  );
  protected readonly hasUnsavedLineAssignments = computed(
    () => !this.sameLineAssignments(this.lineAssignments(), this.savedLineAssignments()),
  );
  protected readonly showSaveButton = computed(
    () => this.showSpecialQuoteTable() && (this.entity()?.sfrStatus === this.submittedConst() || this.isApproved()),
  );

  constructor() {
    this.entityKey = this.data.entityKey as EntityKey;
    this.personLogin = this.data.personLogin as PersonLogin;
    const primaryOrgUnit = this.personLogin.person.primaryOrgUnitId;

    this.trackLoad(this.specialQuoteService.getCompetitors()).subscribe({
      next: (result) => {
        this.competitors.set(result);
        this.competitors.update((current) => [...current].sort((a, b) => a.competitorId - b.competitorId));
      },
      error: (error) => {
        this.handleError(error);
      },
    });

    this.trackLoad(this.specialQuoteService.getSpecialProducts('CLM')).subscribe({
      next: (result) => {
        this.specialProducts.set(result);
        this.specialProducts.update((current) => [...current].sort());
      },
      error: (error) => {
        this.handleError(error);
      },
    });

    if (this.personLoginService.isRsm()) {
      this.isRSMUser = true;
    }
    if (this.personLoginService.hasRole(RoleName.IT)) {
      this.isITUser = true;
    }
    if (this.personLoginService.hasRole(RoleName.ApplicationEngineer)) {
      this.isAppEngUser = true;
    }
    if (primaryOrgUnit && primaryOrgUnit.length > 0) {
      this.trackLoad(this.organizationService.getOrganization(primaryOrgUnit)).subscribe({
        next: (organization) => {
          this.isRepUser.set(organization.typeCode == 'IREP' || organization.typeCode == 'OREP');
          this.specialQuoteRequest().cusno = +organization.erpReferenceId;
        },
        error: (error) => {
          this.handleError(error);
        },
      });
    }

    this.loadEntity();

    this.loadExternalSpecials();
    this.loadExternalSpecialRequests();
    this.loadIsUserCancelable();
    this.loadLineAssignments();
  }

  protected submitRequestLabel(): string {
    return this.isExistingRequestEditable() ? 'Update Request' : 'Submit Request';
  }

  protected updateRequest() {
    this.isBusy.set(true);
    this.specialQuoteRequest().projectName = this.projectName()!;
    this.specialQuoteRequest().numUnitsInProject = this.numUnitsInProject()!;
    this.specialQuoteRequest().projectValue = this.projectValue()!;
    if (this.fileName) {
      this.specialQuoteRequest().fileName = this.fileName;
      this.specialQuoteRequest().file = this.fileContent;
    }
    this.specialQuoteService.updateRequest(this.entityKey.entityId, this.entityKey.entityRevision, this.specialQuoteRequest()).subscribe({
      next: () => {
        this.resetSpecialQuoteRequest();
        this.returnToRequests();
        this.isBusy.set(false);
      },
      error: (error) => {
        this.isBusy.set(false);
        this.handleError(error);
      },
    });
  }

  protected submitOrUpdateRequest() {
    if (this.isExistingRequestEditable()) {
      this.updateRequest();
    } else {
      this.submitRequest();
    }
  }

  protected submitRequest() {
    this.isBusy.set(true);
    this.specialQuoteRequest().projectName = this.projectName()!;
    this.specialQuoteRequest().numUnitsInProject = this.numUnitsInProject()!;
    this.specialQuoteRequest().projectValue = this.projectValue()!;
    this.specialQuoteRequest().fileName = this.fileName;
    this.specialQuoteRequest().file = this.fileContent;
    this.specialQuoteService.submitRequest(this.entityKey.entityId, this.entityKey.entityRevision, this.specialQuoteRequest()).subscribe({
      next: () => {
        this.resetSpecialQuoteRequest();
        this.returnToRequests();
        this.isBusy.set(false);
      },
      error: (error) => {
        this.isBusy.set(false);
        this.handleError(error);
      },
    });
  }

  protected showSaveAndFinalizeButtons(): boolean {
    return (this.isITUser || this.isAppEngUser) && this.showSpecialQuoteTable() && this.selectedExternalSpecials().length > 0;
  }

  /**
   * Cancelling acts on the request itself, so it stays available on a request that has no special quotes
   * attached to it yet - which is every request until a quote number is added to it.
   */
  protected showApplicationEngineerCancelButton(): boolean {
    return (this.isITUser || this.isAppEngUser) && this.showSpecialQuoteTable() && !!this.specialQuoteRequest().quoteNum;
  }

  protected showFinalizeButtonVisible(): boolean {
    return this.showSaveAndFinalizeButtons() && !this.isApproved() && !!this.specialQuoteRequest().quoteNum;
  }

  protected showPreProcessingButtons(): boolean {
    return (
      (this.isITUser || this.isAppEngUser) &&
      this.entity()?.sfrStatus === this.submittedConst() &&
      this.specialQuoteRequest().processingStatus === 'P'
    );
  }

  protected applicationEngineerCancel() {
    this.isBusy.set(true);

    const messages: string[] = [];
    messages.push('This action will cancel the Special Quote Request and cannot be undone.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.specialQuoteService.applicationEngineerCancel(this.entityKey.entityId, this.entityKey.entityRevision, this.openExternalId()).subscribe({
            next: () => {
              this.returnToRequests();
              this.isBusy.set(false);
            },
            error: (error) => {
              this.isBusy.set(false);
              this.handleError(error);
            },
          });
        } else {
          this.isBusy.set(false);
        }
      },
    });
  }

  protected preProcessingAction(processingStatus: 'A' | 'D' | 'E') {
    this.isBusy.set(true);

    const messages: string[] = [];
    messages.push('This action will ' + this.preProcessingActionDescription(processingStatus) + ' the Special Quote Request.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.specialQuoteService.preProcessingAction(this.entityKey.entityId, this.entityKey.entityRevision, processingStatus, this.openExternalId()).subscribe({
            next: () => {
              this.returnToRequests();
              this.isBusy.set(false);
            },
            error: (error) => {
              this.isBusy.set(false);
              this.handleError(error);
            },
          });
        } else {
          this.isBusy.set(false);
        }
      },
    });
  }

  private preProcessingActionDescription(processingStatus: 'A' | 'D' | 'E'): string {
    if (processingStatus === 'A') {
      return 'approve';
    } else if (processingStatus === 'D') {
      return 'deny';
    }
    return 'use an existing quote for';
  }

  protected userCancel() {
    this.isBusy.set(true);

    const messages: string[] = [];
    messages.push('This action will cancel the Special Quote Request and cannot be undone.');
    messages.push('');
    messages.push('Do you want to proceed?');
    this.dialogService.showConfirmationDialog(messages, 'Yes', 'No').subscribe({
      next: (response: boolean) => {
        if (response) {
          this.specialQuoteService.userCancel(this.entityKey.entityId, this.entityKey.entityRevision, this.openExternalId()).subscribe({
            next: () => {
              this.returnToRequests();
              this.isBusy.set(false);
            },
            error: (error) => {
              this.isBusy.set(false);
              this.handleError(error);
            },
          });
        } else {
          this.isBusy.set(false);
        }
      },
    });
  }

  protected showFinalizeButton(): boolean {
    const specials = this.selectedExternalSpecials();
    return (
      specials.length > 0 &&
      specials.every((item) => item.setupFee != null && String(item.setupFee) !== '' && item.pricePerUnit != null && String(item.pricePerUnit) !== '') &&
      specials.every((item) => item.quoteStatus === 'A' && specials.every((item) => item.specialQty && item.specialQty > 0) && this.entity()?.sfrStatus === this.submittedConst())
    );
  }

  /**
   * Assigning special quotes to entity lines is done by IT and application engineering only. Everyone
   * else gets the dialog without tabs.
   */
  protected showLineAssignmentsTab(): boolean {
    return this.isITUser || this.isAppEngUser;
  }

  protected showAddSpecialQuoteSection(): boolean {
    return (this.isITUser || this.isAppEngUser) && (this.isSubmitted() || this.isApproved());
  }

  protected isSubmitted(): boolean {
    return this.entity()?.sfrStatus === this.submittedConst();
  }

  /**
   * Nothing on the request may be edited once it has been submitted, and it stays that way after
   * approval.
   */
  protected showQuoteRequestForm(): boolean {
    return this.showRequestForm();
  }

  /**
   * The quote the form is currently showing - the one a cancel applies to.
   */
  private openExternalId(): string {
    return String(this.specialQuoteRequest().quoteNum);
  }

  /**
   * Returns to the request list and re-reads everything a submit or a cancel can change, so the dialog
   * stays open on up to date data.
   */
  private returnToRequests(): void {
    this.backToRequests();
    this.loadEntity();
    this.loadExternalSpecialRequests();
    this.loadExternalSpecials();
    this.loadIsUserCancelable();
    this.loadLineAssignments();
  }

  protected backToRequests(): void {
    this.clearStagedUpload();
    this.isNewRequest.set(false);
    this.showRequestForm.set(false);
  }

  protected createNewRequest(): void {
    this.resetSpecialQuoteRequest();
    this.isNewRequest.set(true);
    this.showRequestForm.set(true);
  }

  /**
   * Opens an existing request in the form, read only, so the values that were submitted can be reviewed.
   */
  protected showRequest(externalId: string): void {
    this.clearStagedUpload();
    this.trackLoad(
      this.specialQuoteService.getSpecialQuoteRequest(this.entityKey.entityId, this.entityKey.entityRevision, +externalId),
    ).subscribe({
      next: (result) => {
        if (result) {
          this.specialQuoteRequest.set(result);
        }
        this.isNewRequest.set(false);
        this.showRequestForm.set(true);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  /**
   * A request that has been denied or cancelled is finished - nothing further can be submitted or
   * cancelled on it.
   */
  protected isRequestClosed(): boolean {
    const processingStatus = this.specialQuoteRequest().processingStatus;
    return processingStatus === 'C' || processingStatus === 'D';
  }

  /**
   * A request that has been submitted stays editable until it is denied or cancelled.
   */
  protected isExistingRequestEditable(): boolean {
    const processingStatus = this.specialQuoteRequest().processingStatus;
    return (
      !this.isNewRequest() &&
      !!this.specialQuoteRequest().quoteNum &&
      (processingStatus === 'P' || processingStatus === 'A' || processingStatus === 'E')
    );
  }

  protected isReadOnly(): boolean {
    return !this.isNewRequest() && !this.isExistingRequestEditable() && (this.isSubmitted() || this.isApproved());
  }

  protected showSubmitRequestButton(): boolean {
    const projectName = this.projectName();
    const numUnitsInProject = this.numUnitsInProject();
    const projectValue = this.projectValue();
    return !!(
      (this.isNewRequest() || this.isExistingRequestEditable() || this.entity()?.sfrStatus !== this.submittedConst()) &&
      projectName &&
      projectName.length > 0 &&
      this.specialQuoteRequest().numSpecialUnits &&
      this.specialQuoteRequest().numSpecialUnits > 0 &&
      numUnitsInProject &&
      numUnitsInProject > 0 &&
      projectValue &&
      projectValue > 0 &&
      this.specialQuoteRequest().description &&
      this.specialQuoteRequest().description.length > 0 &&
      this.specialQuoteRequest().product &&
      this.specialQuoteRequest().product.length > 0
    );
  }

  private getNumUnitsInProject(entity: Entity | undefined): number | undefined {
    if (entity?.leadTimeCode === 'CSP') {
      return (entity.lines ?? [])
        .filter((line) => line.bestLeadTime === 'CSP')
        .reduce((total, line) => total + (line.quantity ?? 0), 0);
    }
    return entity?.unitQuantity;
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  deleteRow(externalId: string) {
    this.specialQuoteService.deleteExternalSpecial(this.entityKey.entityId, this.entityKey.entityRevision, externalId).subscribe({
      next: () => {
        this.externalSpecials.update((current) => current.filter((item) => item.externalId !== externalId));
        // deleting a special quote also deletes its line assignments on the server
        this.savedLineAssignments.update((current) => this.withoutExternalId(current, externalId));
        this.lineAssignments.update((current) => this.withoutExternalId(current, externalId));
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  public close(): void {
    this.dialogRef.close();
  }

  fileSelected(files: FileList): void {
    if (files && files.length === 1) {
      const file = files[0];
      const reader: FileReader = new FileReader();
      reader.onloadend = () => {
        let binary = '';
        const bytes = new Uint8Array(reader.result as ArrayBuffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        this.fileContent = btoa(binary);
        this.fileName = file.name;
      };
      reader.readAsArrayBuffer(file);
    }
  }

  protected addQuoteNumber() {
    this.errorMessage.set('');

    /*
     * A special quote can only be on the entity once - entity id, revision and quote number are the
     * primary key - so one already attached cannot be moved onto another request. Saying which request
     * holds it beats the button doing nothing.
     */
    const existing = this.externalSpecials().find((item) => item.externalId === this.quoteNumber.toString());
    if (existing) {
      this.errorMessage.set(
        existing.externalSpecialRequestId
          ? `Special Quote ${this.quoteNumber} is already assigned to Special Quote Request ${existing.externalSpecialRequestId}`
          : `Special Quote ${this.quoteNumber} is already assigned`,
      );
    } else {
      this.specialQuoteService.getSpecialQuote(this.entityKey.entityId, this.quoteNumber).subscribe({
        next: (specialQuoteResult) => {
          if (specialQuoteResult) {
            const externalSpecial: ExternalSpecial = new ExternalSpecial();
            externalSpecial.entityId = this.entityKey.entityId;
            externalSpecial.entityRevision = this.entityKey.entityRevision;
            externalSpecial.externalId = this.quoteNumber.toString();
            externalSpecial.original = false;
            externalSpecial.specialQty = 0;
            externalSpecial.quoteStatus = specialQuoteResult.quoteStatus;
            externalSpecial.description = specialQuoteResult.description;
            externalSpecial.setupFee = specialQuoteResult.setupFee;
            externalSpecial.pricePerUnit = specialQuoteResult.pricePerUnit;
            if (this.specialQuoteRequest().quoteNum) {
              externalSpecial.externalSpecialRequestId = String(this.specialQuoteRequest().quoteNum);
            }

            this.specialQuoteService.addExternalSpecial(externalSpecial).subscribe({
              next: () => {
                this.externalSpecials.update((current) => [...current, externalSpecial]);
              },
              error: (error) => {
                this.handleError(error);
              },
            });
          } else {
            this.errorMessage.set('Invalid Quote Number: ' + this.quoteNumber);
          }
        },
        error: (error) => {
          this.handleError(error);
        },
      });
    }
  }

  protected taskTranslation(taskOpen: boolean): string {
    return taskOpen ? 'Open' : 'Closed';
  }

  protected processStatusTranslation(processStatus: string): string {
    if (processStatus && processStatus == 'A') {
      processStatus = 'Approved';
    } else if (processStatus && processStatus == 'D') {
      processStatus = 'Denied';
    } else if (processStatus && processStatus == 'P') {
      processStatus = 'Pending';
    } else if (processStatus && processStatus == 'E') {
      processStatus = 'Existing Quote';
    } else if (processStatus && processStatus == 'C') {
      processStatus = 'User Canceled';
    }

    return processStatus;
  }

  protected quoteStatusTranslation(quoteStatus: string): string {
    if (quoteStatus && quoteStatus == 'A') {
      quoteStatus = 'Approved';
    } else if (quoteStatus && quoteStatus == 'D') {
      quoteStatus = 'Denied';
    } else if (quoteStatus && quoteStatus == 'P') {
      quoteStatus = 'Pending';
    } else if (quoteStatus && quoteStatus == 'C') {
      quoteStatus = 'Cancelled';
    } else if (quoteStatus && quoteStatus == 'H') {
      quoteStatus = 'Hold';
    }

    return quoteStatus;
  }

  protected saveSpecialQuote(finalize: boolean) {
    this.isBusy.set(true);
    const observables: Observable<any>[] = [];

    for (let externalSpecial of this.selectedExternalSpecials()) {
      observables.push(this.specialQuoteService.updateExternalSpecial(externalSpecial));
    }

    forkJoin(observables).subscribe({
      next: () => {
        if (finalize) {
          this.specialQuoteService.finalizeSpecialQuoteRequest(this.entityKey.entityId, this.entityKey.entityRevision, this.openExternalId()).subscribe({
            next: () => {
              this.returnToRequests();
              this.isBusy.set(false);
            },
            error: (error) => {
              this.handleError(error);
              this.isBusy.set(false);
            },
          });
        } else {
          this.isBusy.set(false);
        }
      },
      error: (error) => {
        this.handleError(error);
        this.isBusy.set(false);
      },
    });
  }

  /**
   * The detail dialog only reads, so there is nothing to re-read when it closes.
   */
  protected showRequestDetail(externalSpecial: ExternalSpecial) {
    this.dialogService.showSpecialQuotesDetailDialog(externalSpecial, this.isRepUser(), this.entity().sfrStatus);
  }

  protected submittedConst(): string {
    return 'Submitted';
  }

  protected approvedConst(): string {
    return 'Approved';
  }

  protected isApproved(): boolean {
    return this.entity()?.sfrStatus?.toUpperCase() === this.approvedConst().toUpperCase();
  }

  /**
   * Starts from a new request rather than clearing fields, so nothing from the request last viewed - its
   * quote number or processing status - carries into the next one.
   */
  private resetSpecialQuoteRequest() {
    const specialQuoteRequest = new SpecialQuoteRequest();
    specialQuoteRequest.cusno = this.specialQuoteRequest().cusno;
    this.specialQuoteRequest.set(specialQuoteRequest);
    this.clearStagedUpload();
  }

  /**
   * The attachment a submitted request already carries. It comes back from CLM as base64 alongside the
   * request, so the download needs nothing further from the server.
   */
  protected downloadAttachment(): void {
    const specialQuoteRequest = this.specialQuoteRequest();
    if (!specialQuoteRequest.file) {
      return;
    }
    FileUtils.downloadFile(FileUtils.base64ToBlob(specialQuoteRequest.file), specialQuoteRequest.fileName);
  }

  /**
   * A file picked for one request must not follow the user to another, so the staged upload is dropped
   * whenever the form is left or another request is opened in it.
   */
  private clearStagedUpload(): void {
    this.fileName = null!;
    this.fileContent = null!;
    if (this.fileSelection) {
      this.fileSelection.nativeElement.value = '';
    }
  }

  /**
   * Keeps the busy overlay up until every request the dialog fires has settled, including the per row
   * quote lookups, so the form is never usable while it is still filling in.
   */
  private trackLoad<T>(source: Observable<T>): Observable<T> {
    this.pendingLoads.update((count) => count + 1);
    return source.pipe(finalize(() => this.pendingLoads.update((count) => count - 1)));
  }

  private loadIsUserCancelable(): void {
    this.trackLoad(this.specialQuoteService.isUserCancelable(this.entityKey.entityId, this.entityKey.entityRevision)).subscribe({
      next: (result) => {
        this.isUserCancelable.set(result);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  /**
   * Every request linked to this entity and revision through external_special_request, with the task
   * state and description the services layer resolves alongside it.
   */
  private loadExternalSpecialRequests(): void {
    this.trackLoad(this.specialQuoteService.getExternalSpecialRequests(this.entityKey.entityId, this.entityKey.entityRevision)).subscribe({
      next: (result) => {
        const entries = result ?? [];
        this.externalSpecialRequests.set(
          [...entries].sort((a, b) => +a.externalSpecialRequest.externalId - +b.externalSpecialRequest.externalId),
        );
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  private loadEntity(): void {
    this.trackLoad(this.entityService.getEntityByKey(this.entityKey)).subscribe({
      next: (result) => {
        this.entity.set(result);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  protected selectLine(lineId: string): void {
    this.selectedLineId.set(lineId);
  }

  protected toggleLineAssignment(externalId: string, event: MatCheckboxChange): void {
    const lineId = this.selectedLineId();
    if (!lineId) {
      return;
    }
    this.lineAssignments.update((current) => {
      const assigned = new Set(current.get(lineId) ?? []);
      if (event.checked) {
        assigned.add(externalId);
      } else {
        assigned.delete(externalId);
      }
      return new Map(current).set(lineId, assigned);
    });
  }

  protected saveLineAssignments(): void {
    const assignments: ExternalSpecialLineAssignment[] = [];
    this.lineAssignments().forEach((externalIds, lineId) => {
      externalIds.forEach((externalId) => {
        assignments.push({
          entityId: this.entityKey.entityId,
          entityRevision: this.entityKey.entityRevision,
          entityLine: lineId,
          externalId: externalId,
        });
      });
    });
    this.isBusy.set(true);
    this.specialQuoteService.saveExternalSpecialLineAssignments(this.entityKey.entityId, this.entityKey.entityRevision, assignments).subscribe({
      next: () => {
        this.isBusy.set(false);
        this.loadLineAssignments();
      },
      error: (error) => {
        this.isBusy.set(false);
        this.handleError(error);
      },
    });
  }

  private loadLineAssignments(): void {
    if (!this.showLineAssignmentsTab()) {
      return;
    }
    this.trackLoad(this.specialQuoteService.getExternalSpecialLineAssignments(this.entityKey.entityId, this.entityKey.entityRevision)).subscribe({
      next: (result) => {
        const assignments = new Map<string, ReadonlySet<string>>();
        for (const assignment of result ?? []) {
          assignments.set(assignment.entityLine, new Set([...(assignments.get(assignment.entityLine) ?? []), assignment.externalId]));
        }
        this.savedLineAssignments.set(assignments);
        this.lineAssignments.set(assignments);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  private withoutExternalId(assignments: Map<string, ReadonlySet<string>>, externalId: string): Map<string, ReadonlySet<string>> {
    const updated = new Map<string, ReadonlySet<string>>();
    assignments.forEach((externalIds, lineId) => updated.set(lineId, new Set([...externalIds].filter((id) => id !== externalId))));
    return updated;
  }

  private sameLineAssignments(first: Map<string, ReadonlySet<string>>, second: Map<string, ReadonlySet<string>>): boolean {
    const lineIds = new Set([...first.keys(), ...second.keys()]);
    return [...lineIds].every((lineId) => {
      const firstIds = first.get(lineId) ?? new Set<string>();
      const secondIds = second.get(lineId) ?? new Set<string>();
      return firstIds.size === secondIds.size && [...firstIds].every((id) => secondIds.has(id));
    });
  }

  private loadExternalSpecials(): void {
    this.externalSpecials.set([]);
    this.trackLoad(this.specialQuoteService.getExternalSpecials(this.entityKey.entityId, this.entityKey.entityRevision)).subscribe({
      next: (result) => {
        if (result && result.length > 0) {
          for (let externalSpecial of result) {
            this.trackLoad(this.specialQuoteService.getSpecialQuote(externalSpecial.entityId, +externalSpecial.externalId)).subscribe({
              next: (result) => {
                if (result) {
                  externalSpecial.description = result.description;
                  externalSpecial.quoteStatus = result.quoteStatus;
                  if (externalSpecial.pricePerUnit == null) {
                    externalSpecial.pricePerUnit = result.pricePerUnit;
                  }
                  if (externalSpecial.setupFee == null) {
                    externalSpecial.setupFee = result.setupFee;
                  }
                }
                this.externalSpecials.update((current) => [...current, externalSpecial]);
                this.externalSpecials.update((current) => [...current].sort((a, b) => +a.externalId - +b.externalId));
              },
              error: (error) => {
                this.handleError(error);
              },
            });
          }
        }
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }
}
